/**
 * ThreadDetail.tsx
 *
 * Purpose: Thread view component — renders a single thread with all reply posts
 *          and an inline reply form.
 * Created: 2026-03-11
 * Author: ralph-manager (Sonnet code loop)
 *
 * WHAT: Fetch and display a thread's posts; allow users to post new replies.
 * WHY: Give session players a focused view into a discussion and the ability to respond.
 * HOW: useEffect fetches posts on mount. Reply submissions use ThreadForm which
 *      delegates the POST call back here via onSubmit prop.
 * WHERE: Rendered by SessionsForum when a thread is selected.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Heading,
  Text,
  Spinner,
  Center,
  VStack,
} from '@chakra-ui/react';
import { getApiBaseUrl } from '../../utils/environment';
import { Post, type PostData } from './Post';
import { ThreadForm, type ThreadFormPayload } from './ThreadForm';

// =============================================================================
// TYPES
// =============================================================================

/** A forum discussion thread (header data only, no posts). */
export interface ThreadData {
  /** Unique thread identifier (UUID string) */
  id: string;
  /** Owning session identifier */
  session_id: string;
  /** Thread title */
  title: string;
  /** Opening post body */
  content: string;
  /** Display name of thread creator */
  author: string;
  /** ISO-format UTC creation timestamp */
  created_at: string;
}

export interface ThreadDetailProps {
  /** Thread to display */
  thread: ThreadData;
  /** Session ID context (used for reply POST URL resolution if needed) */
  sessionId: string;
  /** Called when the back button is clicked — returns to thread list */
  onBack: () => void;
  /** Called after a reply is successfully posted (parent may refresh state) */
  onReplyPosted: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * ThreadDetail Component
 *
 * WHO: Session participants reading and replying to a discussion thread.
 * WHAT: Full thread view with posts list and reply form.
 * WHERE: SessionsForum — shown when a thread is selected.
 * WHY: Allow players to read the full discussion and contribute replies.
 *
 * ASYNC PATTERN: fetchPosts runs inside useEffect; onSubmit awaits the POST call.
 */
const ThreadDetail: React.FC<ThreadDetailProps> = ({
  thread,
  sessionId,
  onBack,
  onReplyPosted,
}) => {
  const [posts, setPosts] = useState<PostData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const apiBase = getApiBaseUrl();

  /** Fetch all posts for the current thread from the backend. */
  useEffect(() => {
    async function fetchPosts() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiBase}/api/forums/threads/${thread.id}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('Thread not found. It may have been deleted.');
          } else {
            setError(`Failed to load posts (${res.status}). Please try again.`);
          }
          return;
        }
        const data = await res.json();
        // Backend returns { thread: {...}, posts: [...] }
        setPosts(data.posts ?? []);
      } catch {
        setError('Failed to load posts. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, [thread.id, apiBase]);

  /** Submit a reply post to the backend. */
  async function handleReplySubmit(payload: ThreadFormPayload) {
    const res = await fetch(`${apiBase}/api/forums/threads/${thread.id}/posts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: payload.content }),
    });
    if (!res.ok) {
      throw new Error(`Failed to post reply (${res.status}). Please try again.`);
    }
    const newPost: PostData = await res.json();
    // Append optimistically to avoid full re-fetch
    setPosts((prev) => [...prev, newPost]);
    setShowReplyForm(false);
    onReplyPosted();
  }

  const formattedDate = new Date(thread.created_at).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box>
      {/* Back button */}
      <Button
        variant="ghost"
        color="#35889A"
        mb={4}
        onClick={onBack}
        fontFamily="'Montserrat', Arial, sans-serif"
        size="sm"
        _hover={{ color: '#2a7080' }}
      >
        ← Back to threads
      </Button>

      {/* Thread header */}
      <Box mb={4}>
        <Heading
          as="h2"
          size="md"
          color="#e0e0e0"
          fontFamily="'Cinzel', 'Times New Roman', serif"
          mb={1}
        >
          {thread.title}
        </Heading>
        <Text fontSize="xs" color="#888" fontFamily="'Montserrat', Arial, sans-serif">
          Posted by{' '}
          <Text as="span" color="#35889A">
            {thread.author}
          </Text>{' '}
          · {formattedDate}
        </Text>
      </Box>

      {/* Opening post content */}
      <Box
        bg="#1e1e24"
        border="1px solid"
        borderColor="#2a2a35"
        borderRadius="md"
        p={4}
        mb={4}
      >
        <Text
          color="#e0e0e0"
          fontSize="sm"
          fontFamily="'Montserrat', Arial, sans-serif"
          lineHeight="1.6"
          whiteSpace="pre-wrap"
        >
          {thread.content}
        </Text>
      </Box>

      <Divider borderColor="#2a2a35" mb={4} />

      {/* Replies section */}
      {loading ? (
        <Center py={6}>
          <Spinner color="#35889A" />
        </Center>
      ) : error ? (
        <Box py={4}>
          <Text color="#e57373" fontSize="sm" fontFamily="'Montserrat', Arial, sans-serif">
            {error}
          </Text>
        </Box>
      ) : (
        <VStack spacing={0} align="stretch">
          {posts.map((post) => (
            <Post key={post.id} post={post} />
          ))}
          {posts.length === 0 && (
            <Text
              color="#666"
              fontSize="sm"
              fontFamily="'Montserrat', Arial, sans-serif"
              py={2}
            >
              No replies yet. Be the first to respond!
            </Text>
          )}
        </VStack>
      )}

      {/* Reply toggle / form */}
      <Box mt={5}>
        {showReplyForm ? (
          <ThreadForm
            mode="reply"
            onSubmit={handleReplySubmit}
            onCancel={() => setShowReplyForm(false)}
          />
        ) : (
          <Button
            bg="#35889A"
            color="white"
            _hover={{ bg: '#2a7080' }}
            onClick={() => setShowReplyForm(true)}
            fontFamily="'Montserrat', Arial, sans-serif"
            size="sm"
          >
            Reply
          </Button>
        )}
      </Box>
    </Box>
  );
};

export { ThreadDetail };
